import { DayjsProperty } from '@/lib/jackson'
import { Dayjs } from 'dayjs'
import { JsonProperty, Serializable } from 'ts-jackson'
import { Fromable } from '../model'

/**
 * Wellness & Spa v1 models — mirror the frozen API contract (design §3) exactly.
 *
 * Architecture reminders that leak into these shapes:
 * - A treatment room IS a `rooms` row (created and edited on the Rooms settings page), and a spa
 *   booking IS a `facility_bookings` row + a 1:1 detail row, written against the room's hidden
 *   capacity-1 "booking handle". That is why a booking carries BOTH `roomId` (what staff see and
 *   filter by) and `facilityId` (the handle the existing facility-booking payment endpoints take).
 * - `endedAt` INCLUDES the service's buffer; `serviceEndAt` is the real treatment end. The board
 *   paints the block to `endedAt` (the slot the room is actually held for) and shows
 *   `serviceEndAt` in the detail popover.
 * - Money is THB floats (team convention), never satang.
 */

/* -------------------------------------------------------------------------- */
/* Catalog: categories                                                        */
/* -------------------------------------------------------------------------- */

@Serializable()
export class SpaCategory extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('name')
  name!: string

  @JsonProperty('sortOrder')
  sortOrder!: number

  @JsonProperty('active')
  active!: boolean
}

export interface SpaCategoryPayload {
  name: string
  sortOrder?: number
  active?: boolean
}

/* -------------------------------------------------------------------------- */
/* Catalog: services + duration options                                       */
/* -------------------------------------------------------------------------- */

/**
 * One bookable length of a treatment. Price is FLAT per option (no hour-walk): the booking total
 * is `price × guestCount`, before any staff discount.
 */
@Serializable()
export class SpaServiceDurationOption extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('durationMinutes')
  durationMinutes!: number

  /** Flat THB for one guest at this duration. */
  @JsonProperty('price')
  price!: number

  @JsonProperty('creditAmount')
  creditAmount!: number

  @JsonProperty('sortOrder')
  sortOrder!: number

  @JsonProperty('active')
  active!: boolean
}

/**
 * One line of a treatment's bill of materials — how much of one consumable a single guest uses.
 *
 * `durationOptionId` is the tier this line applies to; NULL means "every duration". A row naming a
 * specific option REPLACES the NULL row for that item at that tier (same precedence rule as
 * package quota coverage), so a 90-minute massage can burn more oil than the 60-minute one without
 * restating every other item.
 *
 * Declared above `SpaServiceItem` on purpose: the `elementType` in that class's decorator is
 * evaluated when the class is defined, so a later declaration would be in its temporal dead zone.
 */
@Serializable()
export class SpaServiceConsumable extends Fromable {
  @JsonProperty('consumableItemId')
  consumableItemId!: string

  /** NULL = applies to every duration option of this treatment. */
  @JsonProperty('durationOptionId')
  durationOptionId!: string | null

  /** Base units consumed per guest (the item's own unit — ml / g / ชิ้น). */
  @JsonProperty('qtyPerGuest')
  qtyPerGuest!: number

  /** Convenience echoes; the CRM resolves them from the catalog when absent. */
  @JsonProperty('name')
  name?: string | null

  @JsonProperty('unit')
  unit?: string | null
}

@Serializable()
export class SpaServiceItem extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('name')
  name!: string

  @JsonProperty('categoryId')
  categoryId!: string

  @JsonProperty('categoryName')
  categoryName?: string

  @JsonProperty('description')
  description?: string | null

  @JsonProperty('imageUrl')
  imageUrl?: string | null

  /** Turnaround minutes appended to every booking's `endedAt` (room held, guest already gone). */
  @JsonProperty('bufferMinutes')
  bufferMinutes!: number

  @JsonProperty('requiresTherapist')
  requiresTherapist!: boolean

  /** Flat THB paid to the therapist per treatment per guest — snapshotted onto the booking. */
  @JsonProperty('therapistFeeAmount')
  therapistFeeAmount?: number | null

  @JsonProperty('bookingLeadTimeMinutes')
  bookingLeadTimeMinutes?: number | null

  @JsonProperty('bookingCancelTimeMinutes')
  bookingCancelTimeMinutes?: number | null

  @JsonProperty('depositRequired')
  depositRequired!: boolean

  @JsonProperty('depositAmount')
  depositAmount?: number | null

  @JsonProperty('active')
  active!: boolean

  @JsonProperty({ path: 'durationOptions', elementType: SpaServiceDurationOption })
  durationOptions!: SpaServiceDurationOption[]

  /** Rooms this treatment can run in — `rooms` ids, the same master a class schedule picks from. */
  @JsonProperty('roomIds')
  roomIds!: string[]

  /** Staff qualified to perform it (the skill matrix). */
  @JsonProperty('therapistUserIds')
  therapistUserIds!: number[]

  /**
   * Bill of materials — what one guest consumes. Optional at the type level so the CRM still
   * renders against a backend that has not shipped the recipe yet.
   */
  @JsonProperty({ path: 'consumables', elementType: SpaServiceConsumable })
  consumables?: SpaServiceConsumable[]
}

/** Recipe line as sent on create/update. PUT replaces the whole set. */
export interface SpaServiceConsumablePayload {
  consumableItemId: string
  /** null = every duration option. */
  durationOptionId: string | null
  qtyPerGuest: number
}

/** Duration option as sent on create/update. `id` present = update that row, absent = insert. */
export interface SpaServiceDurationOptionPayload {
  id?: string
  durationMinutes: number
  price: number
  creditAmount?: number
  sortOrder?: number
  active?: boolean
}

/**
 * Create/update body for a treatment. PUT replaces the options / rooms / therapists sets
 * wholesale (options upsert by id when one is given).
 */
export interface SpaServicePayload {
  name: string
  categoryId: string
  description?: string
  imageUrl?: string
  bufferMinutes?: number
  requiresTherapist?: boolean
  therapistFeeAmount?: number | null
  bookingLeadTimeMinutes?: number | null
  bookingCancelTimeMinutes?: number | null
  depositRequired?: boolean
  depositAmount?: number | null
  active?: boolean
  durationOptions: SpaServiceDurationOptionPayload[]
  roomIds: string[]
  therapistUserIds: number[]
  /** Bill of materials. PUT replaces the whole set, like rooms and therapists. */
  consumables?: SpaServiceConsumablePayload[]
}

/* -------------------------------------------------------------------------- */
/* Catalog: rooms + therapists                                                */
/* -------------------------------------------------------------------------- */

/**
 * A treatment room — a `rooms` row typed RECOVERY_RELAXATION. READ-ONLY here: rooms are created and
 * edited on the Rooms settings page like every other room, where `capacity` is the bed count.
 * `facilityId` is the room's hidden booking handle, null until it is mapped to a treatment.
 */
@Serializable()
export class SpaRoom extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('name')
  name!: string

  @JsonProperty('type')
  type!: string

  /** Beds — the guestCount ceiling for one treatment. */
  @JsonProperty('capacity')
  capacity!: number

  @JsonProperty('status')
  status!: string

  @JsonProperty('facilityId')
  facilityId?: string | null
}

@Serializable()
export class SpaTherapist extends Fromable {
  @JsonProperty('userId')
  userId!: number

  @JsonProperty('name')
  name!: string

  @JsonProperty('nickname')
  nickname?: string | null

  @JsonProperty('imageUrl')
  imageUrl?: string | null
}

/* -------------------------------------------------------------------------- */
/* Availability                                                               */
/* -------------------------------------------------------------------------- */

export interface SpaAvailabilityParams {
  serviceId: string
  durationOptionId: string
  /** YYYY-MM-DD in the branch's local day. */
  date: string
  guestCount?: number
  /** Reschedule only — the booking being moved, so its own slot still shows as free. */
  excludeBookingId?: string
}

/**
 * One 15-minute-grid start the treatment fits into. `endTime` is the SERVICE end (buffer
 * excluded) so the slot chip reads as the guest experiences it; the room is actually held until
 * `endTime + service.bufferMinutes`.
 */
@Serializable()
export class SpaAvailabilitySlot extends Fromable {
  @DayjsProperty()
  startTime!: Dayjs

  @DayjsProperty()
  endTime!: Dayjs

  @JsonProperty('availableRoomIds')
  availableRoomIds!: string[]

  @JsonProperty('availableTherapistIds')
  availableTherapistIds!: number[]
}

/* -------------------------------------------------------------------------- */
/* Bookings                                                                   */
/* -------------------------------------------------------------------------- */

export interface SpaBookingListParams {
  /** YYYY-MM-DD. */
  date: string
  roomId?: string
  therapistUserId?: number
}

/**
 * How the treatment is settled (v1.1).
 * - `PAID` — cash/card/slip at the counter, the v1 behaviour and the default.
 * - `PACKAGE` — one use deducted from a membership quota that covers this treatment. Single guest
 *   only: a quota use belongs to the member who owns it, not to their companions.
 * - `CREDIT` — the duration option's `creditAmount` × guests taken off the member's balance.
 */
export type SpaPaymentMethod = 'PAID' | 'PACKAGE' | 'CREDIT'

export interface CreateSpaBookingPayload {
  spaServiceId: string
  durationOptionId: string
  roomId: string
  /** ISO start of the treatment. */
  startAt: string
  guestCount: number
  /** One therapist per guest when the service requires them; otherwise an empty array. */
  therapistUserIds: number[]
  /** Member booking. Omit for a walk-in guest (send guestName instead). */
  userId?: number
  guestName?: string
  guestPhone?: string
  note?: string
  /** Defaults to PAID server-side; sent explicitly so the choice is never implied. */
  paymentMethod?: SpaPaymentMethod
  /** Required when `paymentMethod` is PACKAGE — the membership whose quota is deducted. */
  userMembershipId?: number
  /** Staff discount on the treatment cash. The BE stores `totalPrice` NET. */
  discountType?: 'AMOUNT' | 'PERCENT'
  discountValue?: number
  slipImageUrl?: string
}

/* -------------------------------------------------------------------------- */
/* จองประจำ — weekly recurring series (v1.3)                                   */
/* -------------------------------------------------------------------------- */

/**
 * Weekly recurring series: the base treatment repeats at the same wall-clock time on the selected
 * weekdays, every week from `startAt` through `endDate` inclusive, all under one
 * `recurringGroupId`. `POST /spa/bookings/recurring` (and `/preview` for the dry run).
 *
 * PAID only in v1 — the BE rejects a PACKAGE/CREDIT series before it judges a single week, because
 * a quota use belongs to one member on one date and cannot be fanned out across a series.
 */
export interface CreateRecurringSpaBookingPayload extends CreateSpaBookingPayload {
  /** Last calendar day of the series, inclusive. `YYYY-MM-DD`, club local time. */
  endDate: string
  /**
   * Weekdays to book every week (0=Sunday … 6=Saturday). The weekday of `startAt` is ALWAYS part
   * of the series whether or not it is listed here — it is the slot the user just picked.
   */
  daysOfWeek?: number[]
  /** true = book the free weeks and report the rest as skipped; false = reject the whole series. */
  skipConflicts?: boolean
}

/** Why one occurrence of a series cannot be booked. Absent ⇔ the week is available. */
export type SpaRecurringUnavailableReason =
  | 'PAST'
  | 'LEAD_TIME'
  | 'ROOM_BUSY'
  | 'ROOM_BLOCKED'
  | 'ROOM_HELD'
  | 'THERAPIST_BUSY'

/** One row of the `POST /spa/bookings/recurring/preview` dry run. */
export interface SpaRecurringOccurrence {
  /** `YYYY-MM-DD`, club local time. */
  date: string
  startAt: string
  /** End of the OCCUPIED window — the treatment plus the service buffer. */
  endAt: string
  available: boolean
  reason?: SpaRecurringUnavailableReason
}

/**
 * An occurrence the caller asked for that did not end up booked. `reason` is deliberately a plain
 * string: the BE's skip vocabulary (`CONFLICT` / `ERROR` / `ABORTED`) is wider than the preview's
 * and may grow, and nothing in the UI branches on it — the counts are what staff act on.
 */
export interface SpaRecurringSkippedOccurrence {
  date: string
  startAt: string
  endAt: string
  reason: string
}

/** Result of `POST /spa/bookings/recurring`. */
export interface SpaRecurringBookingResult {
  recurringGroupId: string
  created: Record<string, unknown>[]
  skipped: SpaRecurringSkippedOccurrence[]
}

/**
 * Result of `DELETE /spa/bookings/recurring/:groupId`. `skipped` counts the occurrences the series
 * cancel deliberately left alone — a treatment already completed or in progress really happened.
 */
export interface SpaCancelSeriesResult {
  recurringGroupId: string
  cancelled: number
  skipped: number
}

/** Move an existing booking. Service / option / price / payment all stay as they are. */
export interface RescheduleSpaBookingPayload {
  /** ISO start of the new treatment slot. */
  startAt: string
  /** Only when the room changes too. */
  roomId?: string
  /** Only when the assignment changes; one therapist per guest, same as create. */
  therapistUserIds?: number[]
}

export interface SpaBookingMembershipParams {
  userId: number
  spaServiceId: string
  durationOptionId: string
}

/**
 * A membership whose quota covers the chosen (treatment, duration) pair — the spa twin of the
 * facility `find-membership` lookup. `remainingUses === null` means unlimited.
 */
@Serializable()
export class SpaBookingMembership extends Fromable {
  @JsonProperty('userMembershipId')
  userMembershipId!: number

  @JsonProperty('packageName')
  packageName!: string

  @JsonProperty('remainingUses')
  remainingUses!: number | null

  @DayjsProperty()
  expiredAt?: Dayjs

  /** True when the package has no use ceiling — `remainingUses` is then meaningless. */
  public isUnlimited(): boolean {
    return this.remainingUses === null || this.remainingUses === undefined
  }

  /** A quota with no uses left cannot pay for a booking; the BE rejects it too. */
  public isUsable(): boolean {
    return this.isUnlimited() || (this.remainingUses ?? 0) > 0
  }
}

@Serializable()
export class SpaBookingMember extends Fromable {
  @JsonProperty('userId')
  userId!: number

  @JsonProperty('name')
  name!: string
}

@Serializable()
export class SpaBookingServiceRef extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('name')
  name!: string

  @JsonProperty('categoryName')
  categoryName?: string | null
}

@Serializable()
export class SpaBookingTherapist extends Fromable {
  @JsonProperty('userId')
  userId!: number

  @JsonProperty('name')
  name!: string

  @JsonProperty('nickname')
  nickname?: string | null
}

/**
 * One tip handed to one therapist on one booking (v1.3). Money is THB like every other spa
 * figure. `method` is a free label the counter picked (CASH / TRANSFER / …), not a closed union:
 * the BE stores whatever it is sent, so a venue that adds a channel must not make this
 * unassignable — it only has to stay readable.
 *
 * Declared above {@link SpaBooking} on purpose — see the note on {@link SpaServiceConsumable}.
 */
@Serializable()
export class SpaBookingTip extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('therapistUserId')
  therapistUserId!: number

  /** The therapist's name as the BE joined it; the sheet falls back to the booking's roster. */
  @JsonProperty('name')
  name?: string | null

  @JsonProperty('amount')
  amount!: number

  @JsonProperty('method')
  method?: string | null
}

/** The board row shape (design §3) — also what POST /spa/bookings returns. */
@Serializable()
export class SpaBooking extends Fromable {
  @JsonProperty('id')
  id!: string

  /** The treatment room — a `rooms` row, the same master a class schedule picks from. */
  @JsonProperty('roomId')
  roomId!: string

  @JsonProperty('roomName')
  roomName?: string

  /** The room's hidden booking handle. Only needed for the facility-side payment endpoints. */
  @JsonProperty('facilityId')
  facilityId!: string

  @DayjsProperty()
  startedAt!: Dayjs

  /** Treatment end + buffer — the span the room is actually blocked for. */
  @DayjsProperty()
  endedAt!: Dayjs

  /** Real treatment end (buffer excluded). */
  @DayjsProperty()
  serviceEndAt?: Dayjs

  @JsonProperty('status')
  status!: string

  @JsonProperty('paymentStatus')
  paymentStatus!: string

  @JsonProperty('totalPrice')
  totalPrice!: number

  @JsonProperty('discountAmount')
  discountAmount?: number

  @JsonProperty('depositAmount')
  depositAmount?: number | null

  /**
   * Where the deposit stands. Typed as a plain string rather than a closed union on purpose: the
   * contract only freezes the two TERMINAL states (`FORFEITED`, `REFUNDED`), so a backend that
   * later adds an intermediate one must not make this field unassignable — it only has to stay
   * readable. Compare case-insensitively via {@link SpaBooking.canForfeitDeposit}.
   */
  @JsonProperty('depositStatus')
  depositStatus?: string | null

  @JsonProperty('paidWith')
  paidWith?: 'CASH' | 'CARD' | 'TRANSFER' | null

  @JsonProperty({ path: 'member', type: SpaBookingMember })
  member?: SpaBookingMember | null

  @JsonProperty('guestName')
  guestName?: string | null

  @JsonProperty('guestPhone')
  guestPhone?: string | null

  @JsonProperty('guestCount')
  guestCount!: number

  @JsonProperty({ path: 'spaService', type: SpaBookingServiceRef })
  spaService!: SpaBookingServiceRef

  /** TRUE treatment minutes (the option's length) — buffer is not included here. */
  @JsonProperty('durationMinutes')
  durationMinutes!: number

  @JsonProperty({ path: 'therapists', elementType: SpaBookingTherapist })
  therapists!: SpaBookingTherapist[]

  @JsonProperty('note')
  note?: string | null

  /** Why the booking was cancelled — only present on CANCELLED rows. */
  @JsonProperty('cancelReason')
  cancelReason?: string | null

  /** Credits charged when the booking was paid with credits (0 otherwise). */
  @JsonProperty('totalCredits')
  totalCredits?: number

  /** Slip url — carried so the detail popover can reuse the facility slip-verify action. */
  @JsonProperty('paymentSlipUrl')
  paymentSlipUrl?: string | null

  /** Set by `POST /spa/bookings/:id/check-in` — the guest is in the room. */
  @DayjsProperty()
  checkInAt?: Dayjs

  /**
   * Set by `POST /spa/bookings/:id/complete` (or the nightly sweep). This is the moment the
   * recipe was deducted, so it is also the flag that blocks cancel and reschedule.
   */
  @DayjsProperty()
  completedAt?: Dayjs

  /**
   * Tips recorded against this booking (v1.3). Optional at the type level like every other v1.3
   * addition: against a backend that has not shipped them the sheet shows no tip section at all,
   * rather than an empty one that reads as "nobody tipped".
   */
  @JsonProperty({ path: 'tips', elementType: SpaBookingTip })
  tips?: SpaBookingTip[]

  /** Sum of {@link tips}, as the BE totalled it — read it rather than re-adding the rows. */
  @JsonProperty('tipTotal')
  tipTotal?: number

  /**
   * Weekly series id, shared by every occurrence booked together (v1.3). Null/absent = a one-off
   * treatment. Its only job here is to unlock the "cancel the rest of the series" action, so it
   * stays optional: against a backend without จองประจำ the sheet simply never offers it.
   */
  @JsonProperty('recurringGroupId')
  recurringGroupId?: string | null

  /** Who to show on a board block: the member, else the walk-in guest. */
  public personLabel(walkInLabel: string): string {
    if (this.member?.name) return this.member.name
    if (this.guestName) return `${this.guestName} (${walkInLabel})`
    return '-'
  }

  /** A finished treatment: no longer actionable, and painted as done on the board. */
  public isCompleted(): boolean {
    return !!this.completedAt
  }

  /** Neither cancelled nor a no-show nor finished — the booking can still be acted on. */
  public isActive(): boolean {
    const status = String(this.status ?? '').toUpperCase()
    return status !== 'CANCELLED' && status !== 'NO_SHOW' && !this.completedAt
  }

  /**
   * The guest is overdue: their treatment was due to start, nobody checked them in, and the
   * booking is still live. Derived from the fields the board already has rather than a server
   * flag, so it re-reads on every board refresh instead of aging in place.
   *
   * `now` is passed in (not read here) so one timestamp classifies the whole day — otherwise
   * rows a millisecond apart could disagree about where "now" is.
   */
  public isLate(now: Dayjs): boolean {
    return this.isActive() && !this.checkInAt && this.startedAt.isBefore(now)
  }

  /**
   * Is there a deposit left to keep? Only a booking that actually took one, and whose deposit has
   * not already reached a terminal state, can have it forfeited — offering the option otherwise
   * would promise money that is already spent or already returned.
   */
  public canForfeitDeposit(): boolean {
    if (!this.depositAmount || this.depositAmount <= 0) return false
    const status = String(this.depositStatus ?? '').toUpperCase()
    return status !== 'FORFEITED' && status !== 'REFUNDED'
  }
}

/**
 * `POST /spa/bookings/:id/no-show`. `forfeitDeposit` carries the answer staff gave when the choice
 * was actually put to them, and is omitted entirely when it was not — a booking with no deposit
 * left to keep must not send `false` and imply a decision was made about one.
 */
export interface NoShowSpaBookingPayload {
  forfeitDeposit?: boolean
}

/* -------------------------------------------------------------------------- */
/* Tips (v1.3)                                                                */
/* -------------------------------------------------------------------------- */

/**
 * `POST /spa/bookings/:id/tips` — one row appended per entry, so two therapists on the same
 * booking are one request and one decision at the counter rather than two that can half-fail.
 * Every therapist named must be assigned to the booking, and every amount must be > 0.
 */
export interface CreateSpaTipsPayload {
  entries: { therapistUserId: number; amount: number }[]
  method?: string
  note?: string
}

/**
 * What both tip endpoints answer with: the booking's FULL tip list and its total, so a caller
 * never has to re-read the booking (or re-add the rows) to know where it now stands.
 */
export class SpaTipsResult extends Fromable {
  tips!: SpaBookingTip[]
  tipTotal!: number
}

/* -------------------------------------------------------------------------- */
/* Therapist shifts (v1.3)                                                    */
/* -------------------------------------------------------------------------- */

/** Inclusive day bounds of a shift range, in the branch's local day. The BE caps the span. */
export interface SpaShiftListParams {
  /** YYYY-MM-DD. */
  startDate: string
  /** YYYY-MM-DD. */
  endDate: string
  userId?: number
}

/**
 * One staff member's rostered stretch on one day — the denominator of their utilisation.
 *
 * `workDate` and the two times stay RAW strings ('YYYY-MM-DD' / 'HH:mm') rather than becoming
 * Dayjs instances: a shift is a wall-clock fact about the branch's own calendar day, and parsing
 * it into a timestamp would invite a timezone shift that moves a late shift onto the wrong date.
 *
 * A therapist does NOT have to appear in any treatment's skill matrix to be rostered — hiring
 * precedes mapping, so the staff picker offers the whole roster.
 */
@Serializable()
export class SpaShift extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('userId')
  userId!: number

  @JsonProperty('name')
  name!: string

  @JsonProperty('nickname')
  nickname?: string | null

  /** 'YYYY-MM-DD'. */
  @JsonProperty('workDate')
  workDate!: string

  /** 'HH:mm'. */
  @JsonProperty('startTime')
  startTime!: string

  /** 'HH:mm'. */
  @JsonProperty('endTime')
  endTime!: string

  @JsonProperty('note')
  note?: string | null

  /** "สมชาย (หนึ่ง)" — the label every spa surface uses for a staff member. */
  public staffLabel(): string {
    return this.nickname ? `${this.name} (${this.nickname})` : this.name
  }
}

/**
 * Create / edit body. The BE rejects an end at or before the start, and any overlap with the same
 * staff member's other shift that day, with a 400 whose message is meant to be shown verbatim.
 */
export interface SpaShiftPayload {
  userId: number
  /** YYYY-MM-DD. */
  workDate: string
  /** HH:mm. */
  startTime: string
  /** HH:mm. */
  endTime: string
  note?: string
}

/* -------------------------------------------------------------------------- */
/* Intake form (v1.3)                                                         */
/* -------------------------------------------------------------------------- */

/** How hard the guest wants to be worked on. Tiles, not a slider — three answers, no scale. */
export type SpaIntakePressureLevel = 'LIGHT' | 'MEDIUM' | 'FIRM'

/**
 * The v1 questionnaire, as a typed FE schema.
 *
 * `answers` is OPAQUE JSON to the backend (validated only as an object), which is exactly why the
 * shape has to be pinned down here: this type is the only contract that says what a stored answer
 * means, so a later question is added by extending it rather than by guessing at a key.
 *
 * Every field is optional — staff fill in what the guest actually told them, and a half-answered
 * form is still worth storing.
 */
export interface SpaIntakeAnswers {
  /** โรคประจำตัว / ประวัติผ่าตัด. */
  medicalConditions?: string
  /** บริเวณที่ต้องเลี่ยง. */
  avoidAreas?: string
  pressureLevel?: SpaIntakePressureLevel
  /** Staff ticked that they read the member's allergy note back to them. */
  allergyAcknowledged?: boolean
  note?: string
}

/**
 * `GET /spa/bookings/:id/intake` → `{ intake: {...} | null }`. Not `@Serializable`: `answers` is
 * a free-form object rather than a decorated class, so it is assembled by the client (which also
 * tolerates a backend that returns the answers at the top level instead of nested).
 */
export class SpaIntakeForm extends Fromable {
  answers!: SpaIntakeAnswers
  /** ชื่อผู้กรอก — who filled the form in, when they signed it. */
  signedName?: string | null
}

/** `PUT /spa/bookings/:id/intake`. Upserts; 409s once the booking has been completed. */
export interface SpaIntakeFormPayload {
  answers: SpaIntakeAnswers
  signedName?: string
}

/* -------------------------------------------------------------------------- */
/* Reports                                                                    */
/* -------------------------------------------------------------------------- */

/** Inclusive day bounds of a report range, in the branch's local day. */
export interface SpaReportSummaryParams {
  /** YYYY-MM-DD. */
  startDate: string
  /** YYYY-MM-DD. */
  endDate: string
}

/** Headline numbers for the whole range — the report page's tile row. */
@Serializable()
export class SpaReportTotals extends Fromable {
  @JsonProperty('bookings')
  bookings!: number

  @JsonProperty('guests')
  guests!: number

  /** Treatment revenue in THB (never satang), net of any staff discount. */
  @JsonProperty('revenue')
  revenue!: number

  /** THB owed to therapists for the treatments in this range. */
  @JsonProperty('therapistFees')
  therapistFees!: number

  @JsonProperty('cancelled')
  cancelled!: number

  @JsonProperty('noShow')
  noShow!: number
}

@Serializable()
export class SpaReportServiceRow extends Fromable {
  @JsonProperty('spaServiceId')
  spaServiceId!: string

  @JsonProperty('serviceName')
  serviceName!: string

  @JsonProperty('categoryName')
  categoryName!: string

  @JsonProperty('bookings')
  bookings!: number

  @JsonProperty('guests')
  guests!: number

  @JsonProperty('revenue')
  revenue!: number

  /**
   * Cost of the consumables this treatment burnt over the range — the SERVICE_USE side of the
   * ledger, attributed to the treatment that caused it.
   *
   * Optional at the type level like every other slice of this report: a backend that has not
   * shipped the margin columns yet must degrade to a zero, never blank the tab.
   */
  @JsonProperty('cogs')
  cogs?: number

  /** `revenue − cogs`, computed by the BE so the CRM and the report can never disagree. */
  @JsonProperty('grossProfit')
  grossProfit?: number
}

@Serializable()
export class SpaReportCategoryRow extends Fromable {
  @JsonProperty('categoryName')
  categoryName!: string

  @JsonProperty('bookings')
  bookings!: number

  @JsonProperty('guests')
  guests!: number

  @JsonProperty('revenue')
  revenue!: number
}

/**
 * Per treatment-room occupancy. `serviceMinutes` is the treatment time actually sold,
 * `openMinutes` the room's opening time over the range; `utilizationPct` is the BE's own
 * ratio of the two — read it rather than recomputing, so the CRM and the BE never disagree.
 */
@Serializable()
export class SpaReportRoomRow extends Fromable {
  @JsonProperty('roomId')
  roomId!: string

  @JsonProperty('roomName')
  roomName!: string

  @JsonProperty('bookings')
  bookings!: number

  @JsonProperty('serviceMinutes')
  serviceMinutes!: number

  @JsonProperty('openMinutes')
  openMinutes!: number

  /** 0-100. */
  @JsonProperty('utilizationPct')
  utilizationPct!: number

  @JsonProperty('revenue')
  revenue!: number
}

@Serializable()
export class SpaReportTherapistRow extends Fromable {
  @JsonProperty('userId')
  userId!: number

  @JsonProperty('name')
  name!: string

  @JsonProperty('nickname')
  nickname?: string | null

  /** Treatments performed — one per guest served, not per booking. */
  @JsonProperty('treatments')
  treatments!: number

  @JsonProperty('serviceMinutes')
  serviceMinutes!: number

  /** THB earned, snapshotted from the treatment's therapist fee. */
  @JsonProperty('fees')
  fees!: number

  /** Rostered minutes over the range — the sum of this user's shifts (v1.3). */
  @JsonProperty('shiftMinutes')
  shiftMinutes?: number

  /**
   * `serviceMinutes / shiftMinutes × 100`, as the BE computed it — read it rather than dividing
   * here, so the two can never disagree. Explicitly NULL when the staff member was never
   * rostered in the range: "we cannot know" is a different answer from "0%", and the report
   * renders it as a dash rather than inventing a ratio out of a zero denominator.
   */
  @JsonProperty('utilizationPct')
  utilizationPct?: number | null

  /** THB tipped over the range, keyed by the BOOKING's start like the fees are (v1.3). */
  @JsonProperty('tips')
  tips?: number
}

/** One day of the range. `date` stays the raw YYYY-MM-DD label — no timezone shifting. */
@Serializable()
export class SpaReportDayRow extends Fromable {
  @JsonProperty('date')
  date!: string

  @JsonProperty('bookings')
  bookings!: number

  @JsonProperty('revenue')
  revenue!: number
}

/**
 * How far ahead a booking was made, as ordered ranges. The keys are the contract — the CRM decides
 * the order they read in and what each one is called, so a bucket the CRM has no label for is
 * simply not shown rather than rendered as a raw key.
 */
export type SpaReportLeadTimeBucketKey = 'under2h' | '2to24h' | '1to3d' | '3to7d' | 'over7d'

/** One lead-time range. The BE always sends all five, zeroes included. */
@Serializable()
export class SpaReportLeadTimeBucket extends Fromable {
  @JsonProperty('key')
  key!: SpaReportLeadTimeBucketKey

  @JsonProperty('bookings')
  bookings!: number
}

/**
 * The gap between when a booking was created and when its treatment starts, in hours to one
 * decimal. Both a mean and a median, because one very early booking skews the first and not the
 * second — a manager reading "do guests plan ahead?" needs to see them disagree.
 */
@Serializable()
export class SpaReportLeadTime extends Fromable {
  @JsonProperty('avgHours')
  avgHours!: number

  @JsonProperty('medianHours')
  medianHours!: number

  @JsonProperty({ path: 'buckets', elementType: SpaReportLeadTimeBucket })
  buckets?: SpaReportLeadTimeBucket[]
}

/**
 * `GET /spa/reports/summary` — one aggregate per date range, sliced five ways. Every breakdown
 * is optional at the type level on purpose: the CRM must still render its tiles and tabs against
 * a backend that has not shipped a slice yet (deploy order is BE first, but a missing array must
 * never blank the page).
 */
@Serializable()
export class SpaReportSummary extends Fromable {
  @JsonProperty({ path: 'totals', type: SpaReportTotals })
  totals!: SpaReportTotals

  @JsonProperty({ path: 'byService', elementType: SpaReportServiceRow })
  byService?: SpaReportServiceRow[]

  @JsonProperty({ path: 'byCategory', elementType: SpaReportCategoryRow })
  byCategory?: SpaReportCategoryRow[]

  @JsonProperty({ path: 'byRoom', elementType: SpaReportRoomRow })
  byRoom?: SpaReportRoomRow[]

  @JsonProperty({ path: 'byTherapist', elementType: SpaReportTherapistRow })
  byTherapist?: SpaReportTherapistRow[]

  @JsonProperty({ path: 'byDay', elementType: SpaReportDayRow })
  byDay?: SpaReportDayRow[]

  /**
   * A sibling of the breakdowns rather than one of them: it answers "how far ahead do guests
   * book?", which no tab slices. Optional like the rest — against a backend that has not shipped
   * it, the report page simply shows no lead-time section at all.
   */
  @JsonProperty({ path: 'leadTime', type: SpaReportLeadTime })
  leadTime?: SpaReportLeadTime
}

/* -------------------------------------------------------------------------- */
/* Consumables: catalog + stock                                               */
/* -------------------------------------------------------------------------- */

/**
 * Why the ledger moved. The signed `qty` on a movement always says which way stock went; the type
 * says why, and is what the history modal groups and colours by.
 * - `RECEIVE` — goods in (+), optionally carrying the purchase cost that feeds the moving average.
 * - `SERVICE_USE` — burnt by a completed treatment (−), with the cost snapshotted at that moment.
 * - `ADJUST` — a manual correction either way (breakage, a miscount, a transfer out).
 * - `STOCK_COUNT` — a physical count; `qty` is the delta applied to reach `countedQty`.
 */
export type ConsumableMovementType = 'RECEIVE' | 'SERVICE_USE' | 'ADJUST' | 'STOCK_COUNT'

/**
 * One stocked consumable of a branch. `onHandQty` is a cache over the movement ledger and MAY GO
 * NEGATIVE on purpose — a treatment is never blocked because the stock card is behind reality, so
 * a negative balance is a real signal to count the shelf, not an error state to hide.
 */
@Serializable()
export class SpaConsumableItem extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('name')
  name!: string

  /** The base unit label the whole ledger is denominated in (ml / g / ชิ้น). */
  @JsonProperty('unit')
  unit!: string

  @JsonProperty('onHandQty')
  onHandQty!: number

  /** Warn at or below this level. Null = the item is never flagged low. */
  @JsonProperty('lowStockThreshold')
  lowStockThreshold?: number | null

  /** THB per base unit — a moving average maintained by RECEIVE movements, not typed by staff. */
  @JsonProperty('costPerUnit')
  costPerUnit?: number | null

  @JsonProperty('active')
  active!: boolean

  /** The BE's own `onHand <= threshold` verdict. */
  @JsonProperty('isLow')
  isLow?: boolean

  /**
   * Should the row carry a warning badge? Negative stock always qualifies — it means the ledger
   * has burnt more than was ever received, which no threshold can express. Otherwise defer to the
   * BE's verdict, falling back to the local comparison when it did not send one.
   */
  public needsRestock(): boolean {
    if (this.onHandQty < 0) return true
    if (typeof this.isLow === 'boolean') return this.isLow
    const threshold = this.lowStockThreshold
    return threshold !== null && threshold !== undefined && this.onHandQty <= threshold
  }
}

/**
 * Create / edit body. `unit` is only editable while the item has no movements — the BE rejects a
 * later change with a 400, because re-denominating a ledger silently would corrupt every past row.
 */
export interface SpaConsumableItemPayload {
  name: string
  unit: string
  lowStockThreshold?: number | null
  active?: boolean
}

/**
 * Goods in. `qty` is ALREADY in base units: the receive modal does the pack arithmetic in the
 * browser (packs × pack size) and sends the product, plus the cost of one base unit, so the BE
 * only ever stores one denomination.
 */
export interface SpaConsumableReceivePayload {
  qty: number
  /** THB per base unit. Omitted leaves the moving average untouched. */
  unitCost?: number
  note?: string
}

/** A manual correction. The note is required — an unexplained delta is unauditable. */
export interface SpaConsumableAdjustPayload {
  qtyDelta: number
  note: string
}

/** A physical count. The BE writes the delta needed to reach `countedQty`. */
export interface SpaConsumableStockCountPayload {
  countedQty: number
  note?: string
}

/** One ledger row. Newest first; the ledger, not `onHandQty`, is the source of truth. */
@Serializable()
export class SpaConsumableMovement extends Fromable {
  @JsonProperty('id')
  id!: string

  @JsonProperty('type')
  type!: ConsumableMovementType

  /** Signed delta in base units. */
  @JsonProperty('qty')
  qty!: number

  /** STOCK_COUNT only — the absolute figure that was counted on the shelf. */
  @JsonProperty('countedQty')
  countedQty?: number | null

  @JsonProperty('unitCost')
  unitCost?: number | null

  /** qty × unitCost, signed. */
  @JsonProperty('totalCost')
  totalCost?: number | null

  /** SERVICE_USE only — the booking whose completion burnt this. */
  @JsonProperty('refFacilityBookingId')
  refFacilityBookingId?: string | null

  @JsonProperty('note')
  note?: string | null

  @JsonProperty('createdByUserId')
  createdByUserId?: number | null

  /** Present when the BE joins the actor; the ledger stays readable without it. */
  @JsonProperty('createdByName')
  createdByName?: string | null

  @DayjsProperty()
  createdAt?: Dayjs
}

export interface SpaConsumableMovementParams {
  page?: number
  pageSize?: number
}

/** A page of the ledger. `total` drives the house pager; it falls back to the page length. */
export class SpaConsumableMovementPage extends Fromable {
  items!: SpaConsumableMovement[]
  total!: number
}

/* -------------------------------------------------------------------------- */
/* Consumables: usage report                                                  */
/* -------------------------------------------------------------------------- */

export interface SpaConsumableUsageParams {
  /** YYYY-MM-DD, inclusive. */
  startDate: string
  /** YYYY-MM-DD, inclusive. */
  endDate: string
}

/** What one item cost the branch over a range — SERVICE_USE only, so it is true COGS. */
@Serializable()
export class SpaConsumableUsageRow extends Fromable {
  @JsonProperty('consumableItemId')
  consumableItemId!: string

  @JsonProperty('name')
  name!: string

  @JsonProperty('unit')
  unit!: string

  /** Base units burnt (a positive figure — the API reports usage, not the signed ledger delta). */
  @JsonProperty('usedQty')
  usedQty!: number

  /** THB, from the unit cost snapshotted on each SERVICE_USE movement. */
  @JsonProperty('usedCost')
  usedCost!: number
}

/**
 * One physical count in the range and what it found. `delta` is the correction the count applied
 * — NEGATIVE means the shelf held less than the ledger claimed (shrinkage), which is the whole
 * reason this table exists. It is the STOCK_COUNT side of the same ledger the usage rows read.
 */
@Serializable()
export class SpaConsumableVarianceRow extends Fromable {
  @JsonProperty('consumableItemId')
  consumableItemId!: string

  @JsonProperty('name')
  name!: string

  @JsonProperty('unit')
  unit!: string

  /** The absolute figure counted on the shelf, in base units. */
  @JsonProperty('countedQty')
  countedQty!: number

  /** Signed: counted − expected. Negative = stock is missing. */
  @JsonProperty('delta')
  delta!: number

  @JsonProperty('note')
  note?: string | null

  @DayjsProperty()
  createdAt?: Dayjs
}

/**
 * `GET /spa/consumables/usage`. `variance` is optional at the type level for the same reason every
 * report slice is: the BE ships in parallel with this client, and a missing array must hide the
 * section rather than blank the tab.
 */
export class SpaConsumableUsageResult extends Fromable {
  items!: SpaConsumableUsageRow[]
  variance!: SpaConsumableVarianceRow[]
}

/* -------------------------------------------------------------------------- */
/* Consumables: what a completed booking burnt                                */
/* -------------------------------------------------------------------------- */

/**
 * One deduction echoed back by `POST /spa/bookings/:id/complete`, so staff see what left the
 * shelf without opening the stock page. `name` / `unit` are optional: when the BE does not echo
 * them the detail sheet resolves the label from the consumables catalog it already caches.
 */
@Serializable()
export class SpaBookingConsumableUse extends Fromable {
  @JsonProperty('consumableItemId')
  consumableItemId!: string

  @JsonProperty('name')
  name?: string | null

  @JsonProperty('unit')
  unit?: string | null

  /** Base units deducted for the WHOLE booking (qtyPerGuest × guestCount), reported positive. */
  @JsonProperty('qty')
  qty!: number

  @JsonProperty('totalCost')
  totalCost?: number | null
}

/**
 * The outcome of completing a booking: when it closed and what it burnt. Serializable so the
 * timestamp is parsed by the same decorator every other spa date uses, rather than a second
 * hand-rolled conversion that could disagree about the timezone.
 */
@Serializable()
export class SpaBookingCompleteResult extends Fromable {
  @DayjsProperty()
  completedAt?: Dayjs

  @JsonProperty({ path: 'consumables', elementType: SpaBookingConsumableUse })
  consumables?: SpaBookingConsumableUse[]
}
